/**
 * Component: Newsletter Form
 * -----------------------------------------------------------------------------
 * Custom Element for a GDPR-compliant newsletter sign-up form.
 * - Handles the enabling and disabling of the submit button based on
 * the state of a "terms and conditions" checkbox.
 *
 * Usage:
 * <newsletter-form class="signup-form">
 *  {%- form 'customer' -%}
 *     <!-- Success message -->
 *
 *     <!-- Error message -->
 *
 *     <!-- Email input -->
 *     <div class="signup-form__inline-container">
 *       <input
 *         type="email"
 *         placeholder="Enter your email"
 *         class="signup-form__email"
 *         name="contact[email]"
 *         aria-label="Email address"
 *         required
 *       />
 *
 *       <!-- Checkbox and label -->
 *       <div class="signup-form__terms">
 *         <input
 *           type="checkbox"
 *           id="agree_terms"
 *           required
 *           data-js-agree-checkbox
 *         />
 *         <label for="agree_terms" style="font-size: 0.875rem;">
 *           I agree to the terms and conditions
 *         </label>
 *       </div>
 *
 *       <!-- Submit button -->
 *       <button
 *         class="button signup-form__button"
 *         type="submit"
 *         data-js-submit-button
 *         disabled
 *       >
 *         Sign Up
 *       </button>
 *     </div>
 *  {%- endform -%}
 * </newsletter-form>
 */

;(() => {
  if (!customElements.get('newsletter-form')) {
    class NewsletterForm extends HTMLElement {
      constructor() {
        super()

        /** @type {HTMLButtonElement | null} */
        this.submitButton = null

        /** @type {HTMLInputElement | null} */
        this.checkbox = null

        this.handleCheckboxChange = this.handleCheckboxChange.bind(this)
      }

      /**
       * Invoked when the element is added to the DOM.
       * Initializes the component and attaches event listeners.
       */
      connectedCallback() {
        this.cacheElements()
        this.attachListeners()
      }

      /**
       * Invoked when the element is removed from the DOM.
       * Cleans up event listeners.
       */
      disconnectedCallback() {
        this.removeListeners()
      }

      /**
       * Caches references to essential DOM elements.
       */
      cacheElements() {
        this.checkbox = this.querySelector('[data-js-agree-checkbox]')
        this.submitButton = this.querySelector('[data-js-submit-button]')
      }

      /**
       * Attaches event listeners for user interactions.
       */
      attachListeners() {
        this.checkbox?.addEventListener('change', this.handleCheckboxChange)
      }

      /**
       * Removes previously attached event listeners.
       */
      removeListeners() {
        this.checkbox?.removeEventListener('change', this.handleCheckboxChange)
      }

      /**
       * Toggles the disabled state of the submit button based on the checkbox state.
       */
      handleCheckboxChange() {
        if (!this.checkbox || !this.submitButton) {
          return
        }
        this.submitButton.disabled = !this.checkbox.checked
      }
    }

    customElements.define('newsletter-form', NewsletterForm)
  }
})()
